'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { SiteConfig, settingsApi } from '@/lib/api';

interface SiteConfigContextType {
  config: SiteConfig | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

// Valeurs par défaut en cas d'erreur
const defaultConfig: SiteConfig = {
  id: 0,
  companyName: 'CERAD-CI',
  companyFullName: 'Centre de Recherche Action pour le Développement',
  slogan: 'Guider le développement en Afrique par les données',
  description: "Cabinet d'expertise spécialisé en Suivi-Évaluation, RSE et gestion de projets.",
  descriptionLong: null,
  phone: '+225 07 00 00 00 00',
  whatsapp: null,
  email: 'contact@cerad-ci.org',
  address: 'Cocody - Riviera Palmeraie',
  city: 'Abidjan',
  country: "Côte d'Ivoire",
  mapLatitude: 5.3599517,
  mapLongitude: -4.0082563,
  mapZoom: 15,
  openingHours: 'Lun - Ven : 8h00 - 18h00',
  facebook: null,
  linkedin: null,
  twitter: null,
  instagram: null,
  youtube: null,
  tiktok: null,
  yearsExperience: 15,
  projectsCount: 200,
  partnersCount: 50,
  founderName: 'Elisée KOUMAN',
  founderTitle: 'Co Fondateur',
  founderBio: null,
  founderImage: null,
  partners: [],
  siteUrl: 'https://cerad-ci.org',
  apiUrl: 'https://api.cerad-ci.org',
  metaTitle: null,
  metaDescription: null,
  updatedAt: new Date().toISOString(),
};

interface SiteConfigProviderProps {
  children: ReactNode;
  initialConfig?: SiteConfig | null;
}

export function SiteConfigProvider({ children, initialConfig }: SiteConfigProviderProps) {
  const [config, setConfig] = useState<SiteConfig | null>(initialConfig || null);
  const [loading, setLoading] = useState(!initialConfig);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await settingsApi.get();
      if (response.success && response.data) {
        setConfig(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch config');
      }
    } catch (err) {
      console.error('Error fetching site config:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Utiliser les valeurs par défaut en cas d'erreur
      if (!config) {
        setConfig(defaultConfig);
      }
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    if (!initialConfig) {
      fetchConfig();
    }
  }, [initialConfig, fetchConfig]);

  const refresh = async () => {
    await fetchConfig();
  };

  return (
    <SiteConfigContext.Provider value={{ config, loading, error, refresh }}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
}

// Hook pour accéder directement à la config avec fallback
export function useConfig() {
  const { config } = useSiteConfig();
  return config || defaultConfig;
}

// Export des valeurs par défaut pour usage côté serveur
export { defaultConfig };
